import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as _ from 'lodash-es';
import Loader from '@lwt-helix/loader';
import Card, { CardContainer } from '@lwt-helix/card';
import { CustomInput } from '@lwt-helix/controls';
import Table from '../../../../../common/components/table/agGrid/Table';
import { tableViews } from '../../../../../common/components/table/agGrid/tableViews';
import { productionTableTabToolTips } from '../../../../constants/agentProductionConstants';
import { getCurrencyFormat, monthAndYearFormat } from '../../../../helpers/dataFormatters';
import { OverviewChartGraph } from '../../../chart/OverviewChartGraph';
import VolumeUnitsToggle from '../../../common/VolumeUnitsToggle';
import {
    calculateChartData,
    chartModes,
    getChartDataIndexes, getOverviewGraphProps
} from '../../../../helpers/agentDataCalculators';
import { SummaryRow } from '../../../chart/SummaryRow';
import { formatTimePeriodInterval, getPropsSummary } from './productionSummaryHelpers';
import { getElementTooltip } from '../../../../helpers/uiHelpers';
import { getAgentHistory } from '../../../../../service/service-gateway';
import { usePDFStore } from '../../../../../store/pdf/store';
import { getDataWithTotalsRow } from '../../../../helpers/agentProductionHelpers';
import TimePeriodSelector from '../TimePeriodSelector';
import { setStateData } from '../../../../../common/helpers/state';
import TableChartToggle, { tableChartModes } from '../../../common/TableChartToggle';
import { SummaryCard } from '../../../chart/SummaryCard';
import { agentProductionTerms as apt } from '../../../../../constants';
import { useCommonStore } from '../../../../../store/store';

const ProductionTab = props => {
    const {
        mlsId,
        agentId,
        timePeriod,
        tableChartMode,
        setState,
        isXamInternalUser,
        state,
        handleClickChartGraph,
        sendToParent,
        isActive,
        productionGraphMode,
        setProductionGraphView,
        productionIsLabelEnabled,
        setProductionLabels,
        setShouldStickyFooterHide,
        currentTabIndex,
        timePeriodSelector,
    } = props;
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [graphMode, setGraphMode] = useState(productionGraphMode);
    const [summaryTimePeriodData, setSummaryTimePeriodData] = useState({});
    const [tableTimePeriodData, setTableTimePeriodData] = useState([]);
    const [currentIntervalTimePeriod, setCurrentIntervalTimePeriod] = useState('');
    const [isShowChartColumnLabels, setIsShowChartColumnLabels] = useState(productionIsLabelEnabled);
    const gridRef = useRef();

    const {
        membership,
        agentMetricsMlsId,
    } = useCommonStore(state => {
        const mlsAgent = state?.agentInfo?.[0]?.mlsAgentId;
        return({
            membership: state.membership,
            agentMetricsMlsId: mlsAgent?.mlsId,
        });
    });

    const {
        isPDFLoading,
        setAgentProfileProductionTableData,
        setProductionSummary,
    } = usePDFStore(state => ({
        isPDFLoading: state.downloadModalStatus.isPDFLoading,
        setAgentProfileProductionTableData: state.setAgentProfileProductionTableData,
        setProductionSummary: state.setProductionSummary,
    }));

    const sortProductionTableData = (data) => {
        const dataSortedByYears = _.groupBy(data, ({ month }) => new Date(month).getFullYear());
        let result = [];
        for (let year in dataSortedByYears) {
            const sortedByMonths = _.groupBy(dataSortedByYears[year], ({ month }) => new Date(month).getMonth());
            for (let obj in sortedByMonths) {
                const arrayMonth = sortedByMonths[obj];
                let objectResult = {
                    id: '',
                    month: '',
                    listUnits: 0,
                    sellUnits: 0,
                    totalUnits: 0,
                    listVolume: 0,
                    sellVolume: 0,
                    totalVolume: 0
                };

                arrayMonth.forEach((el, index) => {
                    objectResult = {
                        ...objectResult,
                        id: el.month,
                        month: el.month,
                        listUnits: objectResult.listUnits + el.listUnits,
                        sellUnits: objectResult.sellUnits + el.sellUnits,
                        totalUnits: el.listUnits + el.sellUnits,
                        listVolume: objectResult.listVolume + el.listVolume,
                        sellVolume: objectResult.sellVolume + el.sellVolume,
                        totalVolume: el.listVolume + el.sellVolume
                    };
                    if (arrayMonth.length - 1 === index) result.push(objectResult);
                });
            }
        }
        return result;
    };

    const getSummaryTableData = (data) => {
        let summaryResult = {
            listVolume: 0,
            listUnits: 0,
            sellVolume: 0,
            sellUnits: 0,
            totalVolume: 0,
            totalUnits: 0
        };
        data.forEach(obj => {
            summaryResult = {
                ...summaryResult,
                listVolume: summaryResult.listVolume + obj.listVolume,
                listUnits: summaryResult.listUnits + obj.listUnits,
                sellVolume: summaryResult.sellVolume + obj.sellVolume,
                sellUnits: summaryResult.sellUnits + obj.sellUnits,
                totalVolume: summaryResult.totalVolume + obj.totalVolume,
                totalUnits: summaryResult.totalUnits + obj.totalUnits
            };
        });
        return summaryResult;
    };

    useEffect(() => {
        if (!agentId || !mlsId) return;

        const loadProductionData = async () => {
            setIsLoading(true);
            const data = await getAgentHistory({ mlsId, agentId });
            setChartData(data);
            setIsLoading(false);
        };

        const handleErrorInFutureTicket = e => e;  // todo - add error handling

        loadProductionData().catch(handleErrorInFutureTicket);

    }, [mlsId, agentId]);

    const handleVolumeUnitsModeChange = (mode) => {
        setProductionGraphView(mode);
        setGraphMode(mode);
    };
    const handleShowLabelsSelection = (mode) => {
        setProductionLabels(mode);
        setIsShowChartColumnLabels(mode);
    };

    const graphData = useMemo(() => calculateChartData(chartData || {}), [chartData]);

    const dataIndexes = useMemo(() => {
        return getChartDataIndexes({ timePeriod, chartData: graphData });
    }, [timePeriod, graphData]);

    const memoChartGraph = useMemo(() => {

        if (!timePeriod || !dataIndexes || !graphData) return null;

        const graphProps = getOverviewGraphProps({
            chartData: graphData || [],
            timePeriod,
            dataIndexes,
            widthPixelsOutsideGraph: 100,
            graphMode,
        });
        const dataToMap = graphData?.slice(dataIndexes.lastPeriodStart, dataIndexes.lastPeriodFinish + 1);
        const timePeriodInterval = [dataToMap[0].month, dataToMap[dataToMap.length - 1].month];
        setSummaryTimePeriodData(getSummaryTableData(dataToMap));
        const sortedData = sortProductionTableData(dataToMap).map(term => ({
            ...term,
            agentId,
            chartMode: tableChartModes.TABLE
        }));
        setTableTimePeriodData(sortedData);
        const formattedSearchResults = getDataWithTotalsRow(sortedData
            ?.map(p => ({...p, month: monthAndYearFormat(p.month)})).reverse());
        setAgentProfileProductionTableData(formattedSearchResults);
        setCurrentIntervalTimePeriod(formatTimePeriodInterval(timePeriodInterval, timePeriod));

        if (isActive) {
            setShouldStickyFooterHide(false);
            const dataForParent = {
                data: sortedData,
                currentIntervalDates: timePeriodInterval,
                gridRef,
                tableChartMode
            };
            sendToParent(dataForParent);
        }

        return (
            <OverviewChartGraph
                {...graphProps}
                isShowColumnLabels={isShowChartColumnLabels}
                handleClickChartGraph={handleClickChartGraph}
                currentTabIndex={currentTabIndex}
            />
        );

    }, [timePeriod, graphMode, chartData, dataIndexes, tableChartMode, isActive, isShowChartColumnLabels]);

    const isDataNotLoaded = isLoading || !dataIndexes;

    const chartGraph = isDataNotLoaded
        ? <Loader />
        : memoChartGraph;

    const getSummaryDetails = (data, tips) => {

        const summaryDetails = [];

        summaryDetails.push({
            volume: data.listVolume,
            units: data.listUnits,
            tip: tips.summaryListSide,
            dataLwtId: 'list'
        });
        summaryDetails.push({
            volume: data.sellVolume,
            units: data.sellUnits,
            tip: tips.summarySellSide,
            dataLwtId: 'sell'
        });
        summaryDetails.push({
            volume: data.totalVolume,
            units: data.totalUnits,
            tip: tips.summaryTotal,
            dataLwtId: 'total'
        });

        return summaryDetails;
    };

    const summaryDetails = getSummaryDetails(summaryTimePeriodData, productionTableTabToolTips);

    const propsSummary = useMemo(() => {
        const summary = graphData && dataIndexes && graphMode
            ? getPropsSummary({ graphData, dataIndexes, graphMode })
            : null;
        setProductionSummary(summary);
        return summary;
    }, [graphData, dataIndexes, graphMode]);
    const shouldShowSummaryRow = !isLoading && timePeriod;

    /* This 'bridge-purpose' component will force 'Table' to re-render all table rows and thereby all the
    * anchor tag urls will contain updated 'intervalType' query according to the timePeriod selected option.
    * When user clicks an updated anchor tag the triggered 'Recruiting_agent_listing_detail_drilldown' endpoint
    * will get data just for the selected timePeriod, e.g. Last6Months, Annually, Last24Months. */
    const TableBridge = ({tableView, rowData, externalParams, tableTitleWithTooltips}) => {
        return (
            <Table
                tableView={tableView}
                rowData={rowData}
                externalParams={externalParams}
                tableTitleWithTooltips={tableTitleWithTooltips}
                gridRef={gridRef}
            />
        )
    };

    return (
        <div className='d-flex flex-column'>
            <div className='controls-container mb-3'>
                <TableChartToggle
                    defaultMode={tableChartMode}
                    parentSetMode={(m) => setStateData('productionMode', m, setState)}
                    mlsName={
                        isXamInternalUser
                            ? membership?.find((p) => p?.mlsId === agentMetricsMlsId)
                                  ?.shortDescription
                            : state.mlsName
                    }
                />
                {tableChartMode === tableChartModes.TABLE && (
                    <span className='font-weight-bold gray-text'>{currentIntervalTimePeriod}</span>
                )}
                <div className='d-flex justify-content-end align-items-center flex-wrap' style={{ gap: '8px' }}>
                    {tableChartMode === tableChartModes.CHART && (
                        <CustomInput
                            dataLwtId='areaPercentageCheckbox'
                            type='checkbox'
                            checked={isShowChartColumnLabels}
                            id='productionLabelsCheckbox'
                            label={
                                graphMode === chartModes.UNITS
                                    ? getElementTooltip(productionTableTabToolTips.unitsCheckBox)
                                    : getElementTooltip(productionTableTabToolTips.volumeCheckBox)
                            }
                            onChange={() => handleShowLabelsSelection(!isShowChartColumnLabels)}
                        />
                    )}
                    <TimePeriodSelector
                        options={timePeriodSelector.timePeriodOptions}
                        currentValue={timePeriodSelector.currentValue}
                        setTimePeriod={timePeriodSelector.setTimePeriod}
                        isDisabled={timePeriodSelector.isDisabled}
                    />
                    {tableChartMode === tableChartModes.CHART && (
                        <VolumeUnitsToggle
                            parentSetMode={handleVolumeUnitsModeChange}
                            defaultMode={graphMode}
                        />
                    )}
                </div>
            </div>
            {tableChartMode === tableChartModes.TABLE ? (
                <>
                    {isPDFLoading && (
                        <div className='pdf-chart pdf-chart-agent-detail'>{chartGraph}</div>
                    )}
                    <div className='mb-3'>
                        <CardContainer
                            dataLwtId='agent-profile-production-card-detail'
                            className='agent-profile-production-summary-table'
                            type='group'
                        >
                            {summaryDetails?.map(({ volume, units, tip, dataLwtId }, index) => (
                                <div key={index} className='summary-card-wrapper'>
                                    <SummaryCard
                                        id={dataLwtId}
                                        value={volume?.toString()}
                                        children={
                                            <>
                                                {getElementTooltip(tip, true)}
                                                <div className='helix-body'>
                                                    <p className='mb-1 text-nowrap helix-display-x-small'>
                                                        {getCurrencyFormat(volume)}
                                                        <br />
                                                    </p>
                                                    <span className='font-weight-bold'>{`${units} ${apt.units}`}</span>
                                                </div>
                                            </>
                                        }
                                    />
                                </div>
                            ))}
                        </CardContainer>
                    </div>
                    <TableBridge
                        tableView={tableViews.agentDetailProduction}
                        rowData={tableTimePeriodData}
                        externalParams={{
                            timePeriod: timePeriod,
                            productionGraphMode: productionGraphMode
                        }}
                        tableTitleWithTooltips={productionTableTabToolTips}
                    />
                </>
            ) : (
                <>
                    <div className='mb-2'>
                        {shouldShowSummaryRow && <SummaryRow {...propsSummary} />}
                    </div>
                    <div className='agent-contact-chart-wrapper py-4 px-2'>{chartGraph}</div>
                    <div className='hidden-export-table'>
                        {/* This class hides the table used as a reference to export the table without being displayed  */}
                        <TableBridge
                            tableView={tableViews.agentDetailProduction}
                            rowData={tableTimePeriodData}
                            externalParams={{
                                timePeriod: timePeriod,
                                productionGraphMode: productionGraphMode
                            }}
                            tableTitleWithTooltips={productionTableTabToolTips}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default ProductionTab;
